import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface User {
  id: number
  email: string
  is_admin: boolean
  subscription_status?: 'active' | 'free' | 'inactive' | 'expired'
  subscription_end_date?: string | null
  emergency_mode?: boolean
  has_paid_initial_fee?: boolean
  ixen_system_access?: boolean
  terms_accepted_at?: string | null
  terms_version_accepted?: string | null
}

const isSubscriptionActive = (user: User): boolean => {
  if (user.subscription_status !== 'active' && user.subscription_status !== 'free') return false
  if (user.subscription_end_date) {
    return new Date(user.subscription_end_date) > new Date()
  }
  return true
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isAdmin: boolean
  isHydrated: boolean
  hasActiveSubscription: boolean
  hasIxenSystemAccess: boolean
  
  setAuth: (user: User, token: string) => void
  updateUser: (user: Partial<User>) => void
  logout: () => void
  checkAuth: () => boolean
  setHydrated: (hydrated: boolean) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isAdmin: false,
      isHydrated: false,
      hasActiveSubscription: false,
      hasIxenSystemAccess: false,
      
      setAuth: (user, token) => {
        set({
          user,
          token,
          isAuthenticated: true,
          isAdmin: user.is_admin,
          hasActiveSubscription: isSubscriptionActive(user),
          hasIxenSystemAccess: user.is_admin || (user.ixen_system_access ?? false)
        })
      },
      
      updateUser: (userData) => {
        const current = get().user
        if (current) {
          const updated = { ...current, ...userData }
          set({
            user: updated,
            isAdmin: updated.is_admin,
            hasActiveSubscription: isSubscriptionActive(updated),
            hasIxenSystemAccess: updated.is_admin || (updated.ixen_system_access ?? false)
          })
        }
      },
      
      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isAdmin: false,
          hasActiveSubscription: false,
          hasIxenSystemAccess: false
        })
      },
      
      checkAuth: () => {
        const state = get()
        return state.isAuthenticated && !!state.token
      },
      
      setHydrated: (hydrated) => {
        set({ isHydrated: hydrated })
      }
    }),
    {
      name: 'auth-storage',
      onRehydrateStorage: () => (state) => {
        if (state?.user) {
          const subActive = isSubscriptionActive(state.user)
          if (state.hasActiveSubscription !== subActive) {
            useAuthStore.setState({ hasActiveSubscription: subActive })
          }
        }
        state?.setHydrated(true)
      }
    }
  )
)
